import React, { useState, useEffect } from 'react';
import { auth } from '../../../../../firebase/config';
import Modal from '../../../../../components/Modal';
import TextField from '../../../../../components/TextField';
import Button from '../../../../../components/Button';
import { FiUser, FiPhone, FiMail, FiBriefcase, FiLock } from 'react-icons/fi';
import Icon from '../../../../../components/Icon';
import { submitPSO, type PSOFormData } from '../service/addStaffService';

type AddStaffModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    bankId?: string;
};

const DESIGNATION_OPTIONS = [
    { value: 'Head', label: 'Head' },
    { value: 'Regional Head', label: 'Regional Head' },
    { value: 'PSO', label: 'PSO' },
    { value: 'Incharge CCTV', label: 'Incharge CCTV' },
    { value: 'Incharge BAMS', label: 'Incharge BAMS' },
    { value: 'Incharge Guarding', label: 'Incharge Guarding' },
    { value: 'Incharge MIS', label: 'Incharge MIS' },
];

// Check if designation is an incharge type
const isInchargeDesignation = (designation: string): boolean => {
    const inchargeDesignations = [
        'Incharge CCTV',
        'Incharge BAMS',
        'Incharge Guarding',
        'Incharge MIS'
    ];
    return inchargeDesignations.includes(designation.trim());
};

export default function AddStaffModal({ isOpen, onClose, onSuccess, bankId }: AddStaffModalProps) {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<PSOFormData>({
        name: '',
        contact: '',
        email: '',
        designation: '',
        password: '',
    });
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (!isOpen) {
            // Reset form when modal closes
            setFormData({
                name: '',
                contact: '',
                email: '',
                designation: '',
                password: '',
            });
            setErrors({});
        }
    }, [isOpen]);

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }
        if (!formData.contact.trim()) {
            newErrors.contact = 'Contact number is required';
        } else if (!/^[0-9+\-\s()]+$/.test(formData.contact.trim())) {
            newErrors.contact = 'Please enter a valid contact number';
        }
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
            newErrors.email = 'Please enter a valid email address';
        }
        if (!formData.designation.trim()) {
            newErrors.designation = 'Designation is required';
        }

        // Validate password for incharge designations
        if (isInchargeDesignation(formData.designation)) {
            if (!formData.password || !formData.password.trim()) {
                newErrors.password = 'Password is required for incharge';
            } else if (formData.password.trim().length < 6) {
                newErrors.password = 'Password must be at least 6 characters';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        const uid = auth.currentUser?.uid;
        if (!uid || !bankId) {
            setErrors({ submit: 'User not authenticated or bank ID missing' });
            return;
        }

        setLoading(true);
        try {
            await submitPSO(bankId, formData);

            // Reset form
            setFormData({
                name: '',
                contact: '',
                email: '',
                designation: '',
                password: '',
            });
            setErrors({});

            onSuccess();
        } catch (err: any) {
            console.error('Error adding staff:', err);
            setErrors({ submit: err.message || 'Failed to add staff. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            name: '',
            contact: '',
            email: '',
            designation: '',
            password: '',
        });
        setErrors({});
        onClose();
    };

    const isIncharge = isInchargeDesignation(formData.designation);

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Add New Staff"
            size="lg"
        >
            <form onSubmit={handleSubmit} className="p-6">
                <div className="space-y-4">
                    <TextField
                        label="Name"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="Enter staff name"
                        required
                        prefix={<Icon icon={FiUser} className="text-gray-400" />}
                        className={errors.name ? 'border-red-500' : ''}
                    />
                    {errors.name && (
                        <p className="-mt-3 text-xs text-red-600 dark:text-red-400">{errors.name}</p>
                    )}

                    <TextField
                        label="Contact"
                        type="tel"
                        value={formData.contact}
                        onChange={(e) => handleInputChange('contact', e.target.value)}
                        placeholder="Enter contact number"
                        required
                        prefix={<Icon icon={FiPhone} className="text-gray-400" />}
                        className={errors.contact ? 'border-red-500' : ''}
                    />
                    {errors.contact && (
                        <p className="-mt-3 text-xs text-red-600 dark:text-red-400">{errors.contact}</p>
                    )}

                    <TextField
                        label="Email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="Enter email address"
                        required
                        prefix={<Icon icon={FiMail} className="text-gray-400" />}
                        className={errors.email ? 'border-red-500' : ''}
                    />
                    {errors.email && (
                        <p className="-mt-3 text-xs text-red-600 dark:text-red-400">{errors.email}</p>
                    )}

                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                            Designation <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <Icon
                                icon={FiBriefcase}
                                className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                            />
                            <select
                                value={formData.designation}
                                onChange={(e) => {
                                    handleInputChange('designation', e.target.value);
                                    // Clear password if designation changes to non-incharge
                                    if (!isInchargeDesignation(e.target.value)) {
                                        setFormData(prev => ({ ...prev, password: '' }));
                                    }
                                }}
                                className={`w-full rounded-xl border py-3 pl-10 pr-4 text-sm outline-none transition bg-white text-gray-900 border-gray-300 shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600 ${errors.designation ? 'border-red-500' : ''}`}
                                required
                            >
                                <option value="">Select Designation</option>
                                {DESIGNATION_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {errors.designation && (
                            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.designation}</p>
                        )}
                    </div>

                    {isIncharge && (
                        <>
                            <TextField
                                label="Password"
                                type="password"
                                value={formData.password || ''}
                                onChange={(e) => handleInputChange('password', e.target.value)}
                                placeholder="Enter password (min 6 characters)"
                                required
                                prefix={<Icon icon={FiLock} className="text-gray-400" />}
                                className={errors.password ? 'border-red-500' : ''}
                            />
                            {errors.password && (
                                <p className="-mt-3 text-xs text-red-600 dark:text-red-400">{errors.password}</p>
                            )}
                            <p className="-mt-3 text-xs text-gray-500 dark:text-gray-400">
                                Password is required for incharge designations. This will create a Google Auth account.
                            </p>
                        </>
                    )}

                    {errors.submit && (
                        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
                            {errors.submit}
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-4">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={loading}>
                            {loading ? 'Adding...' : 'Add Staff'}
                        </Button>
                    </div>
                </div>
            </form>
        </Modal>
    );
}

